import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import { api, errorMessage } from "@/lib/adminApi";
import ReviewList from "@/components/admin/ReviewList";
import AddReview from "@/components/admin/AddReview";
import ImportCsv from "@/components/admin/ImportCsv";
import QuotesTable from "@/components/admin/QuotesTable";
import GoogleSync from "@/components/admin/GoogleSync";
import JoinRequests from "@/components/admin/JoinRequests";

const TABS = [
    { id: "pending", label: "Pending" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "add", label: "Add reviews" },
    { id: "joins", label: "Join forms" },
    { id: "quotes", label: "Quote requests" },
];

export default function AdminDashboard({ user, onSignOut }) {
    const [tab, setTab] = useState("pending");
    const [reviews, setReviews] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [joins, setJoins] = useState([]);

    const load = useCallback(async () => {
        try {
            const [r, q, j] = await Promise.all([api.get("/admin/testimonials"), api.get("/admin/quotes"), api.get("/admin/join-requests")]);
            setReviews(r.data);
            setQuotes(q.data);
            setJoins(j.data);
        } catch (e) {
            toast.error(errorMessage(e));
            if (e?.response?.status === 401) onSignOut();
        }
    }, [onSignOut]);

    useEffect(() => {
        load();
    }, [load]);

    const counts = reviews.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});

    return (
        <div className="mx-auto max-w-6xl px-5 py-8 md:px-8">
            <header className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6">
                <div className="flex items-center gap-4">
                    <Logo className="h-11" />
                    <span className="rounded-full border border-border px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Admin</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="hidden text-xs text-muted-foreground sm:block" data-testid="admin-user-email">{user.email}</span>
                    <Link to="/" className="flex h-9 items-center gap-1.5 rounded-full border border-border px-3 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid="admin-view-site">
                        <ExternalLink size={13} /> Site
                    </Link>
                    <button onClick={onSignOut} data-testid="admin-signout-btn" className="flex h-9 items-center gap-1.5 rounded-full border border-border px-3 text-xs text-muted-foreground transition-colors hover:border-destructive hover:text-destructive">
                        <LogOut size={13} /> Sign out
                    </button>
                </div>
            </header>

            <nav className="mt-6 flex flex-wrap gap-2" data-testid="admin-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        data-testid={`admin-tab-${t.id}`}
                        className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                            tab === t.id ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        {t.label}
                        {counts[t.id] !== undefined && <span className="ml-1.5 font-mono opacity-70">{counts[t.id]}</span>}
                        {t.id === "quotes" && <span className="ml-1.5 font-mono opacity-70">{quotes.length}</span>}
                        {t.id === "joins" && <span className="ml-1.5 font-mono opacity-70">{joins.length}</span>}
                    </button>
                ))}
            </nav>

            <section className="mt-8">
                {["pending", "approved", "rejected"].includes(tab) && (
                    <ReviewList items={reviews.filter((r) => r.status === tab)} onChanged={load} />
                )}
                {tab === "add" && (
                    <div className="grid gap-8 lg:grid-cols-2">
                        <GoogleSync onSynced={() => { load(); setTab("pending"); }} />
                        <AddReview onAdded={() => { load(); setTab("pending"); }} />
                        <ImportCsv onImported={() => { load(); setTab("pending"); }} />
                    </div>
                )}
                {tab === "quotes" && <QuotesTable items={quotes} />}
                {tab === "joins" && <JoinRequests items={joins} onChanged={load} />}
            </section>
        </div>
    );
}
